import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { twMerge } from 'tailwind-merge';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import { CopyIcon } from '@atoms/Icon/CopyIcon';

type CopyToClipboardButtonProps = {
  text?: string | null;
  displayText?: ReactNode;
  fallbackText?: string;
  className?: string;
};

export function CopyToClipboardButton({
  text,
  displayText,
  fallbackText = 'N/A',
  className
}: CopyToClipboardButtonProps) {
  const openNotification = useOpenNotification();

  const success = useCallback(
    (copied: string) => {
      openNotification({
        kind: 'success',
        message: `${copied} has been copied to your clipboard`
      });
    },
    [openNotification]
  );

  if (!hasValue(text)) {
    return <p className="pr-4 text-sm">{fallbackText}</p>;
  }

  const value = text;

  return (
    <CopyToClipboard text={value} onCopy={(copied) => success(copied)}>
      <span
        className={twMerge(
          'inline-block translate-x-[-8px] cursor-pointer rounded-xl p-2 font-medium text-content-brand hover:bg-surface-soft',
          className
        )}
        data-testid="copy-to-clipboard_button_value-btn"
      >
        <span className="inline-flex w-full flex-wrap items-center justify-between gap-2">
          <span className="break-all">{displayText || value}</span>
          <div>
            <CopyIcon
              width={20}
              height={20}
              stroke="auto"
              className="shrink-0"
            />
          </div>
        </span>
      </span>
    </CopyToClipboard>
  );
}
